/**
 * 分层渲染引擎：AvatarConfig → SVG 字符串（PNG 精灵路线）。
 * 图层顺序（后 → 前）：
 *   背景 → 后发 → 脸(含耳+脖子) → 服装 → 胡须 → 岁月痕迹
 *   → 眉 → 眼(iris染瞳色 + frame原色) → 鼻 → 嘴 → 侧发 → 刘海 → 眼镜 → 配饰
 * 有 sprite 的部件走 <image>+染色滤镜；服装/嘴等原色件不挂滤镜。
 * 纯字符串拼装，无 DOM 依赖，将来可直接在游戏内复用（小程序侧也可改为拼 React/DOM）。
 */
import { AvatarConfig } from './config';
import { EYE_COLORS, HAIR_COLORS, LINE, SKINS } from './palettes';
import { ACCESSORIES, AGE_LINES, BEARDS, BROWS, CLOTHES, EYES, FACES, GLASSES, HAIR_BACKS, HAIR_FRONTS, HAIR_SIDES, HAIRS, MOUTHS, NOSES, PartColors } from './parts';

export interface RenderContext {
  config: AvatarConfig;
  colors: PartColors;
}

export function buildContext(config: AvatarConfig): RenderContext {
  const skin = SKINS[config.skin] ?? SKINS[0];
  const hairC = HAIR_COLORS[config.hairColor] ?? HAIR_COLORS[0];
  return {
    config,
    colors: {
      skin: skin.main,
      skinShade: skin.shade,
      hair: hairC.main,
      hairShade: hairC.shade,
      line: LINE,
      iris: EYE_COLORS[config.eyeColor] ?? EYE_COLORS[0],
    },
  };
}

/** hex #rrggbb → 0~1 三通道，供 feColorMatrix 矩阵用 */
const hexTo01 = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16) / 255,
  parseInt(hex.slice(3, 5), 16) / 255,
  parseInt(hex.slice(5, 7), 16) / 255,
];

/** PNG 精灵渲染：灰度 WebP × 目标色 = 染色（feColorMatrix 乘法，sRGB 下计算）。
 *  精灵 1200×1200 映射到 200×200 网格（×6），dataURL 引用以兼容 SVG-as-image 导出。
 *  color 传空串 = 原色件不挂滤镜（眼白/嘴/原色服装等，乘任何色都会偏色）。
 *  `uid`＝这张 SVG 的实例号：同一文档里可能同时挂着多份内联整脸（建角页/造型间/四家店面各自一面镜子、
 *  关掉后仍留在 DOM 里），filter id 只按部件 id 起名的话浏览器全文档取第一个同名 filter，
 *  后画的那份会被别人的发色染色（10-21 实测：隐藏的建角页预览是粉发，医美店后发跟着粉、刘海却黑）。 */
function spriteGroup(uid: string, partId: string, sprite: string, color: string): string {
  const fid = `${uid}-sp-${partId}`;
  if (!color) return `<image x="0" y="0" width="200" height="200" href="${sprite}"/>`;
  const [r, g, b] = hexTo01(color);
  return `<filter id="${fid}" color-interpolation-filters="sRGB"><feColorMatrix type="matrix" values="${r} 0 0 0 0 0 ${g} 0 0 0 0 0 ${b} 0 0 0 0 0 1 0"/></filter><image x="0" y="0" width="200" height="200" href="${sprite}" filter="url(#${fid})"/>`;
}

const spriteColorOf = (c: PartColors, tint?: 'skin' | 'hair'): string =>
  tint === 'hair' ? c.hair : c.skin;

/** 瞳色兜底：iris 染色优先走 colors.iris（eyeColor 槽），无上下文时用此值 */
const EYE_FALLBACK_IRIS = '#6b5d4f';

/** 内联整脸的渲染实例号（只进 filter id，不进存档、不影响画面本身） */
let svgSeq = 0;

export function renderAvatarSVG(config: AvatarConfig): string {
  const { colors: c } = buildContext(config);
  /** 本次渲染的实例号（见 spriteGroup 注释）：每画一张就换，保证同文档内 filter id 不撞 */
  const uid = `av${(++svgSeq).toString(36)}`;
  const isMale = config.gender === 'm';
  const face = FACES[config.face] ?? FACES[0];
  const hairBack = HAIR_BACKS[config.hairBack] ?? HAIR_BACKS[0];
  const hairSides = HAIR_SIDES[config.hairSides] ?? HAIR_SIDES[0];
  const hairFront = HAIR_FRONTS[config.hairFront] ?? HAIR_FRONTS[0];
  // 男发整头一件，画在最上层（脸部区域由源件镂空露出五官）
  const hairMale = HAIRS[config.hair] ?? HAIRS[0];
  const brow = BROWS[config.brow] ?? BROWS[0];
  const eye = EYES[config.eye] ?? EYES[0];
  const nose = NOSES[config.nose] ?? NOSES[0];
  const mouth = MOUTHS[config.mouth] ?? MOUTHS[0];
  const beard = BEARDS[config.beard] ?? BEARDS[0];
  const ageLines = AGE_LINES[config.ageLines] ?? AGE_LINES[0];
  const cloth = CLOTHES[config.cloth] ?? CLOTHES[0];
  const glasses = GLASSES[config.glasses] ?? GLASSES[0];
  const acc = ACCESSORIES[config.acc] ?? ACCESSORIES[0];
  // 共用眉毛按女脸锚位抠件：男脸使用时整体左移 1 网格单位；男专属眉（gender 'm'）不偏
  const browNudge = isMale && brow.gender !== 'm' ? -1 : 0;

  // PNG 精灵路线：脸型部件自带脖子，画在服装之下（领口盖住脖根）。
  // 有 sprite 的部件走 <image>+染色滤镜，没有的回落 render()。
  // 鼻/嘴 Y 微调（noseDy/mouthDy，适配不同脸型）：整层 translate。
    // 眼睛双件：iris 染瞳色（eyeColor 槽）在下，frame 原色在上
    const eyeLayer = eye.sprite
      ? spriteGroup(uid, `${eye.id}-iris`, eye.spriteIris ?? '', c.iris ?? EYE_FALLBACK_IRIS) +
        spriteGroup(uid, `${eye.id}-frame`, eye.sprite, '#ffffff')
      : eye.render(c);
    const layer = (p: { id: string; sprite?: string; spriteTint?: 'skin' | 'hair'; render: (c: PartColors) => string }): string =>
      p.sprite ? spriteGroup(uid, p.id, p.sprite, p.spriteTint ? spriteColorOf(c, p.spriteTint) : '') : p.render(c);
    const layerNudged = (p: { id: string; sprite?: string; spriteTint?: 'skin' | 'hair'; render: (c: PartColors) => string }, dy: number): string => {
      const inner = layer(p);
      return dy ? `<g transform="translate(0 ${dy})">${inner}</g>` : inner;
    };
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
  <rect width="200" height="200" fill="#dcece5"/>
  ${isMale ? '' : `<g>${layer(hairBack)}</g>`}
  <g>${spriteGroup(uid, face.id, face.sprite, spriteColorOf(c, face.spriteTint))}</g>
  <g>${layer(cloth)}</g>
  <g>${layer(beard)}</g>
  <g>${layer(ageLines)}</g>
  ${browNudge ? `<g transform="translate(${browNudge} 0)">${layer(brow)}</g>` : `<g>${layer(brow)}</g>`}
  <g>${eyeLayer}</g>
  <g>${layerNudged(nose, config.noseDy)}</g>
  <g>${layerNudged(mouth, config.mouthDy)}</g>
  <g>${layer(glasses)}</g>
  <g>${layer(acc)}</g>
  ${isMale ? `<g>${layer(hairMale)}</g>` : `<g>${layer(hairSides)}</g><g>${layer(hairFront)}</g>`}
</svg>`;
}

/** 导出 PNG：SVG → dataURL → canvas → 下载（size 为输出边长像素） */
export function exportAvatarPNG(config: AvatarConfig, size = 512): void {
  const svg = renderAvatarSVG(config);
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((png) => {
        if (!png) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(png);
        a.download = 'avatar.png';
        a.click();
        URL.revokeObjectURL(a.href);
      }, 'image/png');
    }
    URL.revokeObjectURL(url);
  };
  img.src = url;
}
